import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type UseIsBasicDigitalV2EnabledArgs = Readonly<{
  /**
   * Whether the template actually carries a `basicDigitalV2` product. Only used to skip
   * the flag read so Amplitude does not record an exposure for a user who could never
   * have been shown the option -- eligibility itself is the product's presence, which
   * card_service decides per template.
   */
  hasBasicDigitalV2Product: boolean;
}>;

type UseIsBasicDigitalV2EnabledResult = Readonly<{
  isEnabled: boolean;
  /**
   * True while the flag is still in flight. Callers must gate their product list on
   * this: the flag *filters* the free product out of the list, and the PDP seeds its
   * selected-product state once, so resolving the flag after that first render would
   * drop a `?format=basicDigitalV2` deep link onto the paper tab.
   */
  loading: boolean;
}>;

/**
 * Basic Digital V2 is a real `stationeryProduct` (`JOYBASICDIGITALV2`), linked to
 * eligible templates by card_service's seed. So eligibility is answered by the product
 * being in `template.products`, and the `printEnableBasicDigitalV2` flag is purely a
 * kill switch over that: off means hide the product, not "do not append one".
 */
export const useIsBasicDigitalV2Enabled = (args: UseIsBasicDigitalV2EnabledArgs): UseIsBasicDigitalV2EnabledResult => {
  const { hasBasicDigitalV2Product } = args;

  const [flagEnabled, flagLoading] = useIsFeatureEnabled('printEnableBasicDigitalV2', { skip: !hasBasicDigitalV2Product });

  return {
    isEnabled: flagEnabled,
    loading: hasBasicDigitalV2Product && flagLoading,
  };
};
